import React from "react";
import {
  AdvancedMarker,
  AdvancedMarkerAnchorPoint,
} from "@vis.gl/react-google-maps";
import { Box, usePrefersReducedMotion } from "@salestrak/ui";

import "./map-markers.css";

type UserLocationMarkerProps = {
  coordinate: { latitude: number; longitude: number };
  /** Degrees clockwise from north. Null or undefined draws no cone. */
  heading?: number | null;
};

const FRAME = 40;
const HALO = 32;
const DOT = 16;
/** The cone's reach from the dot's center; the mask fades it out at the rim. */
const BEAM_RADIUS = 48;

const centered = (size: number) => (FRAME - size) / 2;

export function UserLocationMarker({
  coordinate,
  heading,
}: UserLocationMarkerProps) {
  // Keyframes can't be scoped by the stored motion preference, so the marker
  // picks the variant itself — the hook folds the OS query together with the
  // user's forced setting.
  const reduceMotion = usePrefersReducedMotion();

  // The cone fades out when the heading goes away (a rep stops walking, so
  // the next fix reports none). It has to keep pointing where it last pointed
  // while it fades, or it would swing to north on its way out.
  const hasHeading = typeof heading === "number";
  const lastHeading = React.useRef(0);
  if (hasHeading) lastHeading.current = heading;

  return (
    <AdvancedMarker
      anchorPoint={AdvancedMarkerAnchorPoint.CENTER}
      position={{ lat: coordinate.latitude, lng: coordinate.longitude }}
    >
      {/* The cone, halo and dot are all absolutely positioned, so the frame
        * declares `position="relative"` itself — Box is `position: static` by
        * default and they would escape to the map container. The marker is
        * center-anchored: the dot's center is the coordinate, and the cone
        * rotates about that same point. */}
      <Box
        data-motion={reduceMotion ? "reduced" : undefined}
        height={FRAME}
        position="relative"
        width={FRAME}
      >
        {/* Heading cone: a 60° wedge of accent centered on "up", rotated to
          * the heading, masked so it fades to nothing at the rim. Present only
          * while the fix carries a heading; a wrong cone is worse than none,
          * so null never falls back to north. The angle is the one value only
          * the fix knows, so it travels as a custom property; every other
          * stroke of paint is `map-markers.css`. */}
        <Box
          aria-hidden
          data-heading={hasHeading ? "" : undefined}
          data-slot="user-location-beam"
          height={BEAM_RADIUS * 2}
          left={centered(BEAM_RADIUS * 2)}
          pointerEvents="none"
          position="absolute"
          rounded="full"
          style={{ "--heading": `${lastHeading.current}deg` } as React.CSSProperties}
          top={centered(BEAM_RADIUS * 2)}
          width={BEAM_RADIUS * 2}
        />

        {/* Accent circle that pulses outward and fades. */}
        <Box
          data-slot="user-location-halo"
          height={HALO}
          left={centered(HALO)}
          position="absolute"
          rounded="full"
          top={centered(HALO)}
          width={HALO}
        />

        {/* Static accent core (above the pulse) inside a paper ring with a
          * drop shadow. */}
        <Box
          data-slot="user-location-dot"
          height={DOT}
          left={centered(DOT)}
          position="absolute"
          rounded="full"
          top={centered(DOT)}
          width={DOT}
          zIndex={1}
        />
      </Box>
    </AdvancedMarker>
  );
}
